<template>
  <div>
    <div v-if="!creator.w9_submitted && isPaidCampaign" class="flex p-2 mt-4 flex-row gap-2 bg-warning-100">
      <img class="w-16 h-16" src="/assets/stamp.png" />
      <div class="flex flex-col">
        <p class="font-bold mb-1">Taxpayer Information</p>
        <p v-if="!localSubmitted" class="text-sm">
          In order to participate in our campaigns, we require you to complete a standard W-9
        </p>
        <a
          v-if="!localSubmitted"
          class="font-semibold underline py-2 mb-2"
          target="_blank"
          :href="w9FormUrl"
          @click="submitW9Form"
          >Fill out your W9 form here</a
        >
        <p v-if="localSubmitted" class="font-bold text-sm">Your W9 form may take around 10 minutes to process...</p>
      </div>
    </div>
    <form ref="form" class="block lg:pt-0 lg:mt-0 mt-4 -mx-4 p-4 pb-0" :class="{ 'bg-coral-100': inputIsOpen }">
      <div :class="{ 'h-0 hidden': !inputIsOpen }">
        We will need your instagram handle so that when the content is posted on instagram we automatically flag.
        <div class="my-2 small-label">Example:</div>

        <img class="card-img mb-4" style="max-width: 220px" src="/assets/handle.png" />

        <input
          v-model="userHandle"
          type="text"
          name="instagramHandle"
          class="text-black"
          minlength="3"
          placeholder="Instagram handle"
        />

        <div class="text-coral-300 text-sm mb-4 mt-2" v-text="message"></div>
      </div>

      <div class="w-full flex justify-center mb-3">
        <button
          class="button button-primary"
          v-promise-btn
          :disabled="!creator.w9_submitted && isPaidCampaign"
          :class="{ disabled: !creator.w9_submitted && isPaidCampaign }"
          @click.prevent="joinCampaign"
          v-text="joinButtonLabel"
        />
      </div>
    </form>
  </div>
</template>

<script>
import Vue from 'vue';
import Swal from 'sweetalert2';
import { mapGetters, mapState } from 'vuex';
import { Bus } from '../bus';
import { gtmEvent } from '../services/gtm';

export default Vue.extend({
  name: 'ApplyForm',
  data: () => ({
    inputIsOpen: false,
    localSubmitted: false,
    w9FormUrl: '',
    jotFormIds: {
      prod: '230593613053350',
      stg: '230663082825355',
    },
    message: '',
    userHandle: '',
  }),
  computed: {
    ...mapState('timeline', ['creator', 'campaign']),
    ...mapGetters('timeline', ['preApprovalCampaign', 'isPaidCampaign', 'campaignShouldDeliver']),
    classes() {
      const classes = [];

      if (!this.inputIsOpen) {
        classes.push('h-0 hidden');
      }

      return classes.join(' ');
    },
    joinButtonLabel() {
      if (this.preApprovalCampaign) return 'Apply to campaign';

      return 'Join campaign';
    },
  },
  mounted() {
    this.userHandle = this.creator.instagram_handle;
    this.w9FormUrl = `https://form.jotform.com/${
      window.location.hostname === 'creators.adoreme.com' ? this.jotFormIds.prod : this.jotFormIds.stg
    }?email=${this.creator.email}&userId=${this.creator.id}&campaign_permalink=${this.campaign.slug}`;
    Bus.$on('focusCampaignForm', this.focusCampaignForm);
  },
  destroyed() {
    Bus.$off('focusCampaignForm');
  },
  methods: {
    focusCampaignForm() {
      Bus.$emit('changeCampaignTab', 'story');

      setTimeout(() => {
        document.querySelector('.js-timeline-component').scrollTop;
      }, 100);
    },
    async joinCampaign() {
      this.focusCampaignForm();

      this.inputIsOpen = false;
      if (!this.userHandle || this.userHandle === '') {
        this.userHandle = this.creator.instagram_handle;
      }

      if (!this.userHandle || this.userHandle === '' || this.userHandle.length < 3) {
        this.inputIsOpen = true;

        this.message = 'The instagram handle must be at least 3 characters long';
      } else if (
        (this.campaignShouldDeliver('youtube_video') || this.campaignShouldDeliver('youtube_shorts')) &&
        !this.creator.youtube_channel_url
      ) {
        await Swal.fire({
          title: '<div class="text-lg">We\'re so happy that you want to join this campaign!✨</div>',
          html: "<div class='font-montserrat text-black'> This YouTube 📹 campaign is only available for YouTube content creators. <br>To be able to join, edit your profile by adding your YouTube channel URL!</div>",
          confirmButtonText: '<div class="text-sm">Edit my Profile</div>',
          confirmButtonColor: '#813571',
          cancelButtonText: '<div class="text-sm">Cancel</div>',
          showCancelButton: true,
          showClass: {
            popup: 'swal2-noanimation',
            backdrop: 'swal2-noanimation',
          },
        }).then(async (result) => {
          if (result.value) {
            window.location.href = '/profile/edit#youtube';
          }
        });
      } else if (this.campaignShouldDeliver('tiktok_video') && !this.creator.tiktok_profile_username) {
        await Swal.fire({
          title: '<div class="text-lg">We\'re so happy that you want to join this campaign!✨</div>',
          html: "<div class='font-montserrat text-black'> This TikTok 📹 campaign is exclusively designed for TikTok content creators. <br>To join it, go on your profile and fill your TikTok username.</div>",
          confirmButtonText: '<div class="text-sm">Edit my Profile</div>',
          confirmButtonColor: '#813571',
          cancelButtonText: '<div class="text-sm">Cancel</div>',
          showCancelButton: true,
          showClass: {
            popup: 'swal2-noanimation',
            backdrop: 'swal2-noanimation',
          },
        }).then(async (result) => {
          if (result.value) {
            window.location.href = '/profile/edit#tiktok';
          }
        });
      } else if (this.isPaidCampaign && !this.creator.paypal_address) {
        // No paypal address set

        await Swal.fire({
          title: '<div class="text-lg">We\'re so happy that you want to join this campaign!✨</div>',
          html: "<div class='font-montserrat text-black'>Before that, there is only one more step that you need to complete.<br>You just need to go to your profile and complete your PayPal address.</div>",
          confirmButtonText: '<div class="text-sm">Edit my profile</div>',
          confirmButtonColor: '#813571',
          cancelButtonText: '<div class="text-sm">Cancel</div>',
          showCancelButton: true,
          showClass: {
            popup: 'swal2-noanimation',
            backdrop: 'swal2-noanimation',
          },
        }).then(async (result) => {
          if (result.value) {
            window.location.href = '/profile/edit#payment';
          }
        });
      } else {
        this.inputIsOpen = false;

        const actionText = this.preApprovalCampaign ? 'apply to' : 'join';
        const title = `Are you sure you want to ${actionText} this campaign?`;
        const html = `You may only ${actionText} max 3 campaigns at a time<br/>
<div class="mt-2">
  <div class="mt-4 text-sm text-center">
    By joining the campaign you agree with our <a href="/terms" title="Adore Me Terms &amp; Conditions" target="_blank">Terms &amp; Conditions</a>
    (including possible usage of the content created on our websites or social brand platforms)
  </div>
</div>`;

        await Swal.fire({
          title,
          html,
          icon: 'warning',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          showCancelButton: true,
        }).then(async (result) => {
          if (result.value) {
            await this.submit();
          }

          gtmEvent('join', {
            ga_category: 'campaign',
            ga_action: this.preApprovalCampaign ? 'apply' : 'join',
            ga_label: result.value ? 'yes' : 'no',
          });
        });
      }
    },
    submitW9Form() {
      this.localSubmitted = true;
    },
    async submit() {
      const { data } = await window.axios.post(`/campaigns/${this.campaign.id}/apply`, {
        instagramHandle: this.userHandle,
      });

      if (data && data.success) {
        Bus.$emit('joinedCampaign', {
          campaignApplication: data.campaignApplication,
          campaignOrderGuidelines: data.campaignOrderGuidelines,
        });
      }
    },
  },
});
</script>
